import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import {
  Box,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  Button as MuiButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Button, Spacer } from "common";
import FormEditorDataProvider from "components/FormEditor/FormEditorDataProvider";
import Input from "components/FormEditor/Input";
import I18n from "i18n-js";
import { Fragment } from "react";
import { Document, Page } from "react-pdf";
import { Link } from "react-router-dom";
import PageTransition from "./PageTransition";
import SimpleLoadingOverlay from "./SimpleLoadingOverlay";
import SubmissionSuccessModal from "./SubmissionSuccessModal";
import { useWaiverSubmitState } from "./WaiverSubmitProvider";

const useStyles = makeStyles((theme: any) => ({
  page: {
    "& canvas": {
      border: `solid 1px ${theme.palette.divider}`,
      width: "100% !important",
      maxWidth: "1024px",
      height: "auto !important",
    },
  },
}));

export interface WaiverSubmitFormProps {
  forceMobile?: boolean;
}
export default function WaiverSubmitForm({
  forceMobile,
}: WaiverSubmitFormProps) {
  const {
    token,
    waiver_id,
    sharing,
    setSharing,
    item,
    setItem,
    hasMoreWaivers,
    pages,
    setPages,
    handleLoadSuccess,
    handleLoadError,
    submit,
    previewItem,
    errors,
    setErrors,
    showSuccessModal,
    setShowSuccessModal,
    handleFillAnother,
    formKey,
    loading,
    submitting,
  } = useWaiverSubmitState();

  const classes = useStyles();
  const theme = useTheme();
  const _mdUp = useMediaQuery(theme.breakpoints.up("md"));
  const mdUp = forceMobile ? false : _mdUp;

  return (
    <PageTransition direction={1}>
      <Box sx={{ position: "relative" }}>
        <SimpleLoadingOverlay open={loading} />
        <FormEditorDataProvider
          key={formKey}
          type={item?.type}
          value={item?.fields}
          onChange={evt => {
            setItem({ ...item, fields: evt.target?.value } as any);
          }}
        >
          <CardHeader
            title={item?.name}
            subheader={item?.description}
            titleTypographyProps={{ variant: "subtitle1" }}
            avatar={
              hasMoreWaivers ? (
                <IconButton component={Link} to="..">
                  <ChevronLeftIcon />
                </IconButton>
              ) : undefined
            }
          />
          <CardContent>
            {item?.type === "file" && (
              <>
                <Document
                  key={`pdf-${formKey}`}
                  file={item?.file?.url}
                  onLoadSuccess={handleLoadSuccess}
                  onLoadError={handleLoadError}
                >
                  {pages.map((_p, index) => {
                    return (
                      //@ts-ignore
                      <Page
                        key={`${index}`}
                        pageNumber={index + 1}
                        className={classes.page}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        width={595 * 2}
                        height={841 * 2}
                        //className={"pdf-modal-page" + (index > 0 ? " margin-top-2" : "")}
                      />
                    );
                  })}
                </Document>
                <Spacer h={2} />
              </>
            )}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                margin: -1,
              }}
            >
              {item?.fields?.map((block, blockIndex) => {
                return (
                  <Fragment key={block.id}>
                    {block.rows?.map((row, rowIndex) => {
                      return (
                        <Box
                          key={row.id}
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            flex: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          {row.fields?.map((field, fieldIndex) => {
                            return (
                              <Box
                                key={field.id}
                                sx={{
                                  maxWidth: mdUp
                                    ? field.width
                                      ? `${field.width}%`
                                      : "initial"
                                    : "100%",
                                  minWidth: mdUp
                                    ? field.width
                                      ? `${field.width}%`
                                      : "initial"
                                    : "100%",
                                  /*maxWidth: {
                                  xs: "100%",
                                  sm: field.width
                                    ? `${field.width}%`
                                    : "initial",
                                },
                                minWidth: {
                                  xs: "100%",
                                  sm: field.width
                                    ? `${field.width}%`
                                    : "initial",
                                },*/
                                  flex: 1,
                                  p: 1,
                                }}
                              >
                                <Input
                                  field={field}
                                  coordinates={{
                                    blockIndex,
                                    rowIndex,
                                    fieldIndex,
                                  }}
                                  preview
                                  error={
                                    errors &&
                                    errors[blockIndex] &&
                                    errors[blockIndex][rowIndex] &&
                                    errors[blockIndex][rowIndex][fieldIndex]
                                      ? errors[blockIndex][rowIndex][fieldIndex]
                                      : undefined
                                  }
                                />
                              </Box>
                            );
                          })}
                        </Box>
                      );
                    })}
                  </Fragment>
                );
              })}
            </Box>
          </CardContent>
          {!previewItem && (
            <CardActions>
              <MuiButton
                component={Link}
                to={`/sharing/${token}`}
                variant="outlined"
                disabled={submitting}
              >
                {I18n.t("App.cancel")}
              </MuiButton>
              <Spacer right />
              <Button
                title="Submit"
                onClick={() => submit()}
                disabled={submitting}
                startIcon={
                  submitting ? <CircularProgress size={20} /> : undefined
                }
              />
            </CardActions>
          )}
        </FormEditorDataProvider>

        <SubmissionSuccessModal
          open={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          onFillAnother={handleFillAnother}
          hasMoreWaivers={hasMoreWaivers}
          token={token}
          countdownSeconds={10}
        />
      </Box>
    </PageTransition>
  );
}
